import { on } from 'js/lib/events'
import { wait, debounce } from 'js/lib/general-utils'
import { registerFunctionalComponent } from 'js/lib/components/component'
import { useListener } from 'js/lib/dynamic/listener'
import { requestNative } from 'js/lib/api/api-request'
import { turbo } from 'js/lib/turbo'
import { notifyBugsnag } from 'js/lib/bugsnag'
import { withDynamicFrameRequestLifecycle } from './events.mjs'

// Global time to wait before executing another fetch
const GLOBAL_EVENT_DEBOUNCE = 10
// How long to wait on the network request before showing the fallback UI (if it exists)
const FALLBACK_DELAY = 220
// How long to show the fallback for if it gets shown
const MIN_FALLBACK_VISIBLE = 1000
// used to tell the server which frame initiated the request
const REQUESTER_KEY_PARAM = '_dynamic_frame_requester_key'
const LOADED_KEYS_PARAM = '_dynamic_frame_loaded_keys'
const loadedFrameKeys = new Set()

registerFunctionalComponent('.js-c-frame', 'DynamicFrame', DynamicFrame)

function DynamicFrame(component) {
  const key = component.element.id

  const closestForm = () => component.element.closest('form')
  const eventForm = event => {
    if (event?.target instanceof HTMLFormElement) return event.target

    return event?.target?.closest?.('form')
  }
  const errorElement = () => component.element.querySelector('.js-c-frame-error')
  const errorReloadButton = () => errorElement().querySelector('.js-frame-reload')
  const contentElement = () => component.element.querySelector('.js-c-frame-content')

  // Fallback elements can be any that does not fall within another frame.
  const fallbackElements = () =>
    [...component.element.querySelectorAll('.js-c-frame-fallback')].filter(
      el => el.closest('.js-c-frame') === component.element
    )

  const handleRefreshTrigger = debounce(
    (event, matchedTrigger) => refresh(event, matchedTrigger),
    GLOBAL_EVENT_DEBOUNCE,
    true
  )

  component.useHandler(() => useComponentListener(handleRefreshTrigger))
  component.useHandler(() => on('click', errorReloadButton, handleRefreshTrigger))

  if (isLazyLoad() && !isAlreadyLoaded()) {
    refresh()
  } else {
    markAsLoaded()
  }

  // Refreshes the frame by submiting an ajax request to the most appropriate url (the frame src,
  // the nearest form, or the window location).
  async function refresh(event, matchedTrigger) {
    const form = closestForm() || eventForm(event)
    const refreshFn = form
      ? () => refreshByFormSubmit(event, form, matchedTrigger)
      : () => refreshByGet(matchedTrigger)
    let fallbackShownAt = null
    setBusy(true)

    const fallbackTimer = setTimeout(() => {
      // show the fallback and hide the error to make the experience nice
      if (showFallback()) {
        fallbackShownAt = Date.now()
        hideError()
      }
    }, FALLBACK_DELAY)

    try {
      await withDynamicFrameRequestLifecycle({
        trigger: matchedTrigger,
        frameKey: key,
        request: async () => {
          // Refresh handlers return a commit function so fallback timing can finish before applying the result.
          const commit = await refreshFn()
          clearTimeout(fallbackTimer)

          if (fallbackShownAt) {
            const elapsed = Date.now() - fallbackShownAt
            if (elapsed < MIN_FALLBACK_VISIBLE) await wait(MIN_FALLBACK_VISIBLE - elapsed)
            hideFallback()
          }

          // Clear frame busy before replacement so containing modals observe the terminal state.
          setBusy(false)
          commit()
        },
      })
    } catch (error) {
      notifyBugsnag(error)
      clearTimeout(fallbackTimer)

      // Hide the fallback if there is one and show the error
      hideFallback()
      showError()
    } finally {
      setBusy(false)
    }
  }

  function useComponentListener(callback) {
    if (!component.element.dataset.trigger) return () => {}

    const listenerAst = JSON.parse(component.element.dataset.trigger)
    const { onChange } = useListener(listenerAst)

    return onChange(callback)
  }

  async function refreshByGet(matchedTrigger) {
    const data = {
      [REQUESTER_KEY_PARAM]: key,
      [LOADED_KEYS_PARAM]: serializedLoadedFrameKeys(),
    }

    const requestMethod = requestMethodFor(matchedTrigger, null, null, component.element.dataset.requestMethod || 'get')

    return await refreshRequest(data, requestMethod, null, matchedTrigger)
  }

  // Refreshes by submitting the form - if the form method is set, use that
  // as the request method.
  async function refreshByFormSubmit(event, form, matchedTrigger) {

    // important to inlcude the submitting button if there is one - so that the form data can include
    // name/value from the button submission.
    const formData = new FormData(form, event?.submitter)
    formData.append(REQUESTER_KEY_PARAM, key)
    formData.append(LOADED_KEYS_PARAM, serializedLoadedFrameKeys())

    const reqType = requestMethodFor(matchedTrigger, formData, form)

    return await refreshRequest(formData, reqType, form, matchedTrigger)
  }

  // Rails requires a special request format for turbo streams. We return a commit function
  // that will render the stream when called so we can orchestrate showing/hiding the fallback.
  async function refreshRequest(data, type, form, matchedTrigger) {
    const { result: html } = await requestNative({
      url: getFrameUrl(form, matchedTrigger),
      type,
      data,
      accept: 'text/vnd.turbo-stream.html',
    })

    return () => {
      markAsLoaded()
      turbo.renderStreamMessage(html)
    }
  }

  function isLazyLoad() {
    return component.element.dataset.load === 'lazy'
  }

  function isAlreadyLoaded() {
    return component.element.dataset.loaded === 'true'
  }

  function markAsLoaded() {
    if (!key) return
    loadedFrameKeys.add(key)
  }

  function serializedLoadedFrameKeys() {
    return [...loadedFrameKeys].join(',')
  }

  function requestMethodFor(matchedTrigger, formData, form, fallbackMethod = 'POST') {
    const triggerRequestMethod = matchedTrigger?.dataset?.dynamicFrameRequestMethod
    if (triggerRequestMethod) return triggerRequestMethod.toUpperCase()

    return (formData?.get('_method') || form?.getAttribute('method') || fallbackMethod).toUpperCase()
  }

  function getFrameUrl(form, matchedTrigger) {
    const triggerSrcUrl = matchedTrigger?.dataset?.dynamicFrameSrc
    if (triggerSrcUrl) return triggerSrcUrl

    const srcUrl = component.element.dataset.src
    if (srcUrl) return srcUrl

    if (form) return form.getAttribute('action') || window.location.href
    return window.location.href
  }

  function showFallback() {
    const fallback = fallbackElements()

    if (!fallback || fallback.length === 0) return false

    if (shouldHideContentOnFallback()) hideContent()
    showElement(fallback)
    return true
  }

  function hideFallback() {
    const fallback = fallbackElements()
    if (!fallback) return

    hideElement(fallback)
    showContent()
  }

  function showError() {
    showElement(errorElement())
  }

  function hideError() {
    hideElement(errorElement())
  }

  function showContent() {
    showElement(contentElement())
  }

  function hideContent() {
    hideElement(contentElement())
  }

  function shouldHideContentOnFallback() {
    const test = component.element.dataset.hideContentOnFallback
    if (test === undefined || test === '' || test === 'true') {
      return true
    }

    return false
  }

  function setBusy(isBusy) {
    component.element.dataset.busy = isBusy ? 'true' : 'false'
    component.element.setAttribute('aria-busy', isBusy ? 'true' : 'false')
    component.element.dispatchEvent(new CustomEvent('dynamic-frame:busy', { bubbles: true }))
  }

}

export function closestBusyDynamicFrame(element) {
  return (
    element.closest('.js-c-frame[aria-busy="true"]') ||
    element.querySelector('.js-c-frame[aria-busy="true"]')
  )
}

// Works for single nodes AND node lists
function showElement(el) {
  if (!el) return
  if (el instanceof Element) el = [el]

  for (const node of el) {
    node.style.display = ''
    node.removeAttribute('aria-hidden')
  }
}

// Works for single nodes AND node lists
function hideElement(el) {
  if (!el) return
  if (el instanceof Element) el = [el]

  for (const node of el) {
    node.style.display = 'none'
    node.setAttribute('aria-hidden', 'true')
  }
}
